<template>
  <div>
    <!-- فلتر البحث -->
    <b-card class="mb-2">
      <b-card-text>
        <b-row class="align-items-end">
          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              label="من تاريخ الدفع"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.paid_at_from"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              label="إلى تاريخ الدفع"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.paid_at_to"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              label="المستخدمون"
              class="mb-md-0"
            >
              <v-select
                v-model="filters.user_ids"
                :options="userOptions"
                :disabled="loadingUsers"
                multiple
                label="name"
                :reduce="(u) => u.id"
                :close-on-select="false"
                dir="rtl"
                placeholder="اختر المستخدمين..."
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              label="اسم المريض"
              class="mb-md-0"
            >
              <b-form-input
                v-model="filters.patient_name"
                placeholder="مثال: علي"
                trim
              />
            </b-form-group>
          </b-col>

          <b-col
            md="1"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              label="رقم الفاتورة"
              class="mb-md-0"
            >
              <b-form-input
                v-model="filters.invoice_no"
                placeholder="مثال: 123"
                trim
              />
            </b-form-group>
          </b-col>

          <b-col
            md="2"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              label="نوع الفاتورة"
              class="mb-md-0"
            >
              <v-select
                v-model="filters.invoice_type"
                :options="invoiceTypeOptions"
                label="label"
                :reduce="(t) => t.value"
                dir="rtl"
                placeholder="جميع الأنواع"
                :clearable="true"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="1"
            cols="12"
            class="d-flex align-items-end"
          >
            <div
              class="mb-2 d-flex flex-wrap"
              style="gap: 0.5rem"
            >
              <b-button
                variant="primary"
                :disabled="loading"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="ml-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="ml-25"
                />
                بحث
              </b-button>
              <b-button
                variant="outline-secondary"
                :disabled="loading"
                @click="resetFilters"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                  class="ml-25"
                />
                إعادة
              </b-button>
            </div>
          </b-col>
        </b-row>

        <div
          v-if="activeFilterLabel"
          class="active-filter-pill mt-2"
        >
          <feather-icon
            icon="FilterIcon"
            size="12"
            class="ml-25"
          />
          {{ activeFilterLabel }}
        </div>
      </b-card-text>
    </b-card>

    <!-- الإحصائيات -->
    <b-card
      v-if="statistics"
      class="mb-2"
    >
      <b-card-text>
        <b-row>
          <b-col
            md="4"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">إجمالي الفواتير</small>
              <strong class="d-block mt-50">{{ statistics.invoice_count }}</strong>
            </div>
          </b-col>
          <b-col
            md="4"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">فواتير الصفحة</small>
              <strong class="d-block mt-50">{{ statistics.page_invoice_count }}</strong>
            </div>
          </b-col>
          <b-col
            md="4"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">المبلغ الإجمالي</small>
              <strong class="d-block mt-50 text-primary">{{ formatIQDCurrency(statistics.total_amount) }}</strong>
            </div>
          </b-col>
          <b-col
            md="4"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">إجمالي الخصومات</small>
              <strong class="d-block mt-50 text-warning">{{ formatIQDCurrency(statistics.total_discount) }}</strong>
            </div>
          </b-col>
          <b-col
            md="4"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">إجمالي الاسترداد</small>
              <strong class="d-block mt-50 text-danger">{{ formatIQDCurrency(refundAmount(statistics)) }}</strong>
            </div>
          </b-col>
          <b-col
            md="4"
            lg="2"
            cols="6"
            class="mb-2"
          >
            <div class="border rounded p-2 text-center h-100">
              <small class="text-muted d-block">الصافي بعد الاسترداد</small>
              <strong class="d-block mt-50 text-success">{{ formatIQDCurrency(netAfterRefund(statistics)) }}</strong>
            </div>
          </b-col>
        </b-row>
      </b-card-text>
    </b-card>

    <!-- الجدول الرئيسي -->
    <b-card>
      <b-card-text>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h3 class="mb-0">
            كشف الحساب اليومي
          </h3>
          <div
            v-if="pagination"
            class="text-muted"
            style="font-size: 0.85rem"
          >
            صفحة {{ pagination.current_page }} من {{ pagination.last_page }}
            · {{ pagination.total }} سجل
          </div>
        </div>

        <b-table
          :items="dailyBreakdown"
          :fields="dailyColumns"
          :busy="loading"
          small
          responsive
          striped
          hover
          show-empty
          empty-text="لا توجد بيانات"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner
                small
                class="ml-50"
              />
              <span>جاري التحميل...</span>
            </div>
          </template>

          <template #cell(date)="data">
            <span class="font-weight-bold">{{ data.item.date }}</span>
          </template>

          <template #cell(invoice_count)="data">
            <b-badge variant="light-primary">
              {{ data.item.invoice_count }} فاتورة
            </b-badge>
          </template>

          <template #cell(total_amount)="data">
            <span class="font-weight-bold text-primary">{{ formatIQDCurrency(data.item.total_amount) }}</span>
          </template>

          <template #cell(total_discount)="data">
            <span :class="data.item.total_discount > 0 ? 'font-weight-bold text-warning' : 'text-muted'">
              {{ data.item.total_discount > 0 ? formatIQDCurrency(data.item.total_discount) : '—' }}
            </span>
          </template>

          <template #cell(total_refund_amount)="data">
            <span :class="refundAmount(data.item) > 0 ? 'font-weight-bold text-danger' : 'text-muted'">
              {{ refundAmount(data.item) > 0 ? formatIQDCurrency(refundAmount(data.item)) : '—' }}
            </span>
          </template>

          <template #cell(total_net_amount)="data">
            <span class="font-weight-bold text-success">{{ formatIQDCurrency(netAfterRefund(data.item)) }}</span>
          </template>

          <template #cell(actions)="data">
            <b-button
              variant="flat-info"
              size="sm"
              @click="openDayModal(data.item)"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
                class="ml-25"
              />
              عرض الفواتير
            </b-button>
          </template>
        </b-table>

        <small
          v-if="dailyBreakdown.length"
          class="text-muted"
        >
          يتم عرض {{ dailyBreakdown.length }} يوم
        </small>

        <!-- ترقيم الصفحات -->
        <div
          v-if="pagination && pagination.last_page > 1"
          class="d-flex justify-content-center mt-3"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="filters.per_page"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            @change="onPageChange"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </div>
      </b-card-text>
    </b-card>

    <!-- مودال فواتير اليوم -->
    <b-modal
      v-if="selectedDay"
      v-model="dayModalVisible"
      size="xl"
      ok-only
      ok-title="إغلاق"
      ok-variant="outline-secondary"
      no-close-on-backdrop
      scrollable
    >
      <template #modal-header="{ close }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            فواتير يوم {{ selectedDay.date }}
          </h5>
          <div
            class="d-flex align-items-center"
            style="gap: 0.5rem"
          >
            <b-badge
              variant="light-primary"
              class="font-small-3"
            >
              {{ selectedDay.invoice_count }} فاتورة
            </b-badge>
            <b-badge
              variant="light-success"
              class="font-small-3"
            >
              صافي بعد الاسترداد: {{ formatIQDCurrency(netAfterRefund(selectedDay)) }}
            </b-badge>
            <b-badge
              v-if="refundAmount(selectedDay) > 0"
              variant="light-danger"
              class="font-small-3"
            >
              استرداد: {{ formatIQDCurrency(refundAmount(selectedDay)) }}
            </b-badge>
            <b-badge
              variant="light-primary"
              class="font-small-3"
            >
              إجمالي: {{ formatIQDCurrency(selectedDay.total_amount) }}
            </b-badge>
            <b-button
              variant="flat-secondary"
              size="sm"
              @click="close()"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-button>
          </div>
        </div>
      </template>

      <!-- ملخص اليوم -->
      <b-row class="mb-2">
        <b-col
          md="2"
          cols="6"
        >
          <div class="border rounded p-1 text-center">
            <small class="text-muted d-block">عدد الفواتير</small>
            <strong>{{ selectedDay.invoice_count }}</strong>
          </div>
        </b-col>
        <b-col
          md="2"
          cols="6"
        >
          <div class="border rounded p-1 text-center">
            <small class="text-muted d-block">المبلغ الإجمالي</small>
            <strong class="text-primary">{{ formatIQDCurrency(selectedDay.total_amount) }}</strong>
          </div>
        </b-col>
        <b-col
          md="2"
          cols="6"
          class="mt-1 mt-md-0"
        >
          <div class="border rounded p-1 text-center">
            <small class="text-muted d-block">إجمالي الخصومات</small>
            <strong class="text-warning">{{ formatIQDCurrency(selectedDay.total_discount) }}</strong>
          </div>
        </b-col>
        <b-col
          md="2"
          cols="6"
          class="mt-1 mt-md-0"
        >
          <div class="border rounded p-1 text-center">
            <small class="text-muted d-block">إجمالي الاسترداد</small>
            <strong :class="refundAmount(selectedDay) > 0 ? 'text-danger' : 'text-muted'">{{ formatIQDCurrency(refundAmount(selectedDay)) }}</strong>
          </div>
        </b-col>
        <b-col
          md="4"
          cols="12"
          class="mt-1 mt-md-0"
        >
          <div class="border rounded p-1 text-center">
            <small class="text-muted d-block">الصافي بعد الاسترداد</small>
            <strong class="text-success">{{ formatIQDCurrency(netAfterRefund(selectedDay)) }}</strong>
          </div>
        </b-col>
      </b-row>

      <!-- جدول الفواتير -->
      <b-table
        :items="selectedDay.invoices"
        :fields="invoiceColumns"
        small
        responsive
        striped
        hover
        show-empty
        empty-text="لا توجد فواتير لهذا اليوم"
      >
        <template #cell(invoice_no)="data">
          <span class="font-weight-bold text-primary">#{{ data.item.invoice_no }}</span>
        </template>

        <template #cell(type_label)="data">
          <b-badge :variant="invoiceTypeVariant(data.item.type)">
            {{ invoiceTypeAr(data.item.type) }}
          </b-badge>
        </template>

        <template #cell(patient_name)="data">
          <div>
            <span class="font-weight-bold">{{ data.item.patient_name }}</span>
            <div>
              <small class="text-muted">بطاقة #{{ data.item.patient_card_no }}</small>
            </div>
          </div>
        </template>

        <template #cell(service)="data">
          <div>
            <span>{{ data.item.service_name || data.item.surgery_name || '—' }}</span>
            <div v-if="data.item.service_type">
              <small class="text-muted">{{ data.item.service_type }}</small>
            </div>
          </div>
        </template>

        <template #cell(amount)="data">
          <span>{{ formatIQDCurrency(data.item.amount) }}</span>
        </template>

        <template #cell(discount)="data">
          <span :class="data.item.discount > 0 ? 'text-warning font-weight-bold' : 'text-muted'">
            {{ data.item.discount > 0 ? formatIQDCurrency(data.item.discount) : '—' }}
          </span>
        </template>

        <template #cell(net_amount)="data">
          <span class="font-weight-bold text-success">{{ formatIQDCurrency(data.item.net_amount) }}</span>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusAr(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(paid_by)="data">
          <span>{{ data.item.paid_by ? data.item.paid_by.name : '—' }}</span>
        </template>

        <template #cell(paid_at)="data">
          <span style="font-size: 0.82rem">{{ formatDateTime(data.item.paid_at) }}</span>
        </template>

        <template #cell(actions)="data">
          <b-button
            variant="flat-primary"
            size="sm"
            @click="openInvoiceDetail(data.item)"
          >
            <feather-icon
              icon="FileTextIcon"
              size="14"
            />
          </b-button>
        </template>
      </b-table>

      <template #modal-footer="{ close }">
        <b-button
          variant="outline-secondary"
          @click="close()"
        >
          إغلاق
        </b-button>
      </template>
    </b-modal>

    <!-- مودال تفاصيل الفاتورة -->
    <b-modal
      v-if="selectedInvoice"
      v-model="invoiceDetailVisible"
      size="md"
      ok-only
      ok-title="إغلاق"
      ok-variant="outline-secondary"
    >
      <template #modal-header="{ close }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <div
            class="d-flex align-items-center"
            style="gap: 0.5rem"
          >
            <h5 class="mb-0">
              تفاصيل الفاتورة
            </h5>
            <b-badge variant="light-secondary">
              #{{ selectedInvoice.invoice_no }}
            </b-badge>
            <b-badge :variant="invoiceTypeVariant(selectedInvoice.type)">
              {{ invoiceTypeAr(selectedInvoice.type) }}
            </b-badge>
            <b-badge :variant="statusVariant(selectedInvoice.status)">
              {{ statusAr(selectedInvoice.status) }}
            </b-badge>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            @click="close()"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
          </b-button>
        </div>
      </template>

      <!-- صافي المبلغ -->
      <div class="amount-highlight text-center p-2 mb-2 rounded">
        <small class="text-muted d-block mb-25">صافي المبلغ</small>
        <h2 class="text-success font-weight-bolder mb-0">
          {{ formatIQDCurrency(selectedInvoice.net_amount) }}
        </h2>
        <div
          v-if="selectedInvoice.discount > 0"
          class="mt-25"
        >
          <small class="text-warning">خصم: {{ formatIQDCurrency(selectedInvoice.discount) }}</small>
          <small class="text-muted mr-50">(الأصلي: {{ formatIQDCurrency(selectedInvoice.amount) }})</small>
        </div>
      </div>

      <!-- بيانات المريض -->
      <div class="mb-1">
        <small
          class="text-uppercase text-muted font-weight-bolder d-block mb-50"
          style="letter-spacing: 0.06em"
        >
          بيانات المريض
        </small>
        <div class="border rounded overflow-hidden">
          <detail-row
            label="الاسم"
            :value="selectedInvoice.patient_name"
          />
          <detail-row
            label="رقم البطاقة"
            :value="selectedInvoice.patient_card_no"
          />
          <detail-row
            label="رقم المريض"
            :value="selectedInvoice.patient_id"
            mono
          />
        </div>
      </div>

      <!-- الخدمة -->
      <div class="mb-1">
        <small
          class="text-uppercase text-muted font-weight-bolder d-block mb-50"
          style="letter-spacing: 0.06em"
        >
          الخدمة
        </small>
        <div class="border rounded overflow-hidden">
          <detail-row
            label="اسم الخدمة"
            :value="selectedInvoice.service_name || '—'"
          />
          <detail-row
            label="نوع الخدمة"
            :value="selectedInvoice.service_type || '—'"
          />
          <detail-row
            label="العملية الجراحية"
            :value="selectedInvoice.surgery_name || '—'"
          />
        </div>
      </div>

      <!-- التواريخ -->
      <div class="mb-1">
        <small
          class="text-uppercase text-muted font-weight-bolder d-block mb-50"
          style="letter-spacing: 0.06em"
        >
          التواريخ
        </small>
        <div class="border rounded overflow-hidden">
          <detail-row
            label="تاريخ الدفع"
            :value="formatDateTime(selectedInvoice.paid_at)"
          />
          <detail-row
            label="تاريخ الاعتماد"
            :value="formatDateTime(selectedInvoice.approved_at)"
          />
        </div>
      </div>

      <!-- الموظفون -->
      <div class="mb-1">
        <small
          class="text-uppercase text-muted font-weight-bolder d-block mb-50"
          style="letter-spacing: 0.06em"
        >
          الموظفون
        </small>
        <div class="border rounded overflow-hidden">
          <detail-row
            label="أنشأ بواسطة"
            :value="selectedInvoice.created_by ? selectedInvoice.created_by.name : '—'"
          />
          <detail-row
            label="اعتمد بواسطة"
            :value="selectedInvoice.approved_by ? selectedInvoice.approved_by.name : '—'"
          />
          <detail-row
            label="دفع بواسطة"
            :value="selectedInvoice.paid_by ? selectedInvoice.paid_by.name : '—'"
          />
        </div>
      </div>

      <!-- المرجع -->
      <div class="mb-1">
        <small
          class="text-uppercase text-muted font-weight-bolder d-block mb-50"
          style="letter-spacing: 0.06em"
        >
          مرجع
        </small>
        <div class="border rounded overflow-hidden">
          <detail-row
            label="معرّف الفاتورة"
            :value="selectedInvoice.id"
            mono
          />
        </div>
      </div>

      <template #modal-footer="{ close }">
        <b-button
          variant="outline-secondary"
          @click="close()"
        >
          إغلاق
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BSpinner,
  BBadge,
  BPagination,
  BModal,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const DetailRow = {
  name: 'DetailRow',
  props: {
    label: String,
    value: [String, Number],
    mono: Boolean,
  },
  template: `
      <div
        class="d-flex justify-content-between align-items-center px-1 py-75"
        style="border-bottom: 1px solid #f5f5f5"
      >
        <small class="text-muted">{{ label }}</small>
        <small
          class="font-weight-bold text-right"
          :style="mono ? 'font-family: monospace; word-break: break-all; max-width: 60%' : 'max-width: 60%; text-align: right'"
        >{{ (value !== null && value !== undefined && value !== '') ? value : '—' }}</small>
      </div>
    `,
}

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BSpinner,
    BBadge,
    BPagination,
    BModal,
    Datepicker,
    vSelect,
    DetailRow,
  },

  data() {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

    return {
      filters: {
        paid_at_from: firstDayOfMonth,
        paid_at_to: today,
        user_ids: [],
        patient_name: '',
        invoice_no: '',
        invoice_type: null,
        service_type_id: '',
        per_page: 30,
      },
      currentPage: 1,

      userOptions: [],
      loadingUsers: false,
      loading: false,

      reportData: null,
      selectedDay: null,
      dayModalVisible: false,
      selectedInvoice: null,
      invoiceDetailVisible: false,

      dailyColumns: [
        { key: 'date', label: 'التاريخ', sortable: true },
        { key: 'invoice_count', label: 'عدد الفواتير', sortable: true },
        { key: 'total_amount', label: 'المبلغ الإجمالي', sortable: true },
        { key: 'total_discount', label: 'الخصومات', sortable: true },
        { key: 'total_refund_amount', label: 'إجمالي الاسترداد', sortable: true },
        { key: 'total_net_amount', label: 'الصافي بعد الاسترداد', sortable: true },
        { key: 'actions', label: '', sortable: false },
      ],

      invoiceColumns: [
        { key: 'invoice_no', label: 'رقم الفاتورة', sortable: true },
        { key: 'type_label', label: 'النوع', sortable: true },
        { key: 'patient_name', label: 'المريض', sortable: true },
        { key: 'service', label: 'الخدمة / العملية', sortable: false },
        { key: 'amount', label: 'المبلغ', sortable: true },
        { key: 'discount', label: 'الخصم', sortable: true },
        { key: 'net_amount', label: 'الصافي', sortable: true },
        { key: 'status', label: 'الحالة', sortable: true },
        { key: 'paid_by', label: 'دفع بواسطة', sortable: false },
        { key: 'paid_at', label: 'وقت الدفع', sortable: true },
        { key: 'actions', label: '', sortable: false },
      ],
    }
  },

  computed: {
    invoiceTypeOptions() {
      const typeMap = {
        invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
        invoice_nicu: 'cashiers.invoice-type-nicu',
        invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
        refund: 'cashiers.invoice-type-refund',
        invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
        invoice_emergency: 'cashiers.invoice-type-emergency',
      }
      return Object.keys(typeMap).map(key => ({
        value: key,
        label: this.$t(typeMap[key]),
      }))
    },
    allDailyBreakdown() {
      return this.reportData?.daily_breakdown || []
    },
    dailyBreakdown() {
      const start = (this.currentPage - 1) * this.filters.per_page
      const end = start + this.filters.per_page
      return this.allDailyBreakdown.slice(start, end)
    },
    statistics() {
      return this.reportData?.statistics || null
    },
    pagination() {
      const total = this.allDailyBreakdown.length
      const perPage = this.filters.per_page || 30
      const lastPage = Math.max(Math.ceil(total / perPage), 1)
      const currentPage = Math.min(this.currentPage, lastPage)

      return {
        current_page: currentPage,
        last_page: lastPage,
        total,
      }
    },
    activeFilterLabel() {
      const parts = []
      const from = this.filters.paid_at_from ? this.formatDateForQuery(this.filters.paid_at_from) : null
      const to = this.filters.paid_at_to ? this.formatDateForQuery(this.filters.paid_at_to) : null
      if (from && to) parts.push(`${from} ← ${to}`)
      else if (from) parts.push(`من: ${from}`)
      else if (to) parts.push(`إلى: ${to}`)
      if (this.filters.patient_name) parts.push(`المريض: ${this.filters.patient_name}`)
      if (this.filters.invoice_no) parts.push(`فاتورة: ${this.filters.invoice_no}`)
      const n = Array.isArray(this.filters.user_ids) ? this.filters.user_ids.length : 0
      if (n > 0) parts.push(`${n} مستخدم محدد`)
      if (this.filters.invoice_type) {
        const found = this.invoiceTypeOptions.find(t => t.value === this.filters.invoice_type)
        if (found) parts.push(`النوع: ${found.label}`)
      }
      return parts.length ? parts.join(' · ') : null
    },
  },

  async created() {
    await this.loadUserOptions()
    this.getReportData()
  },

  methods: {
    applyFilters() {
      this.currentPage = 1
      this.getReportData()
    },

    resetFilters() {
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      this.filters.paid_at_from = firstDayOfMonth
      this.filters.paid_at_to = today
      this.filters.user_ids = []
      this.filters.patient_name = ''
      this.filters.invoice_no = ''
      this.filters.invoice_type = null
      this.filters.service_type_id = ''
      this.currentPage = 1
      this.getReportData()
    },

    onPageChange(page) {
      this.currentPage = page
    },

    openDayModal(day) {
      this.selectedDay = day
      this.dayModalVisible = true
    },

    openInvoiceDetail(invoice) {
      this.selectedInvoice = invoice
      this.invoiceDetailVisible = true
    },

    invoiceTypeAr(type) {
      const found = this.invoiceTypeOptions.find(t => t.value === type)
      return found?.label || type
    },

    invoiceTypeVariant(type) {
      const map = {
        invoice_out_clinic: 'light-success',
        invoice_insurance_surgery: 'light-info',
        invoice_nicu: 'light-danger',
        invoice_patient_card_test: 'light-primary',
        invoice_emergency: 'light-danger',
        refund: 'light-warning',
      }
      return map[type] || 'light-secondary'
    },

    statusAr(status) {
      const map = {
        paid: 'مدفوع',
        unpaid: 'غير مدفوع',
        pending: 'معلق',
      }
      return map[status] || status
    },

    statusVariant(status) {
      const map = {
        paid: 'light-success',
        unpaid: 'light-danger',
        pending: 'light-warning',
      }
      return map[status] || 'light-secondary'
    },

    buildQueryString() {
      const params = []
      const add = (k, v) => {
        if (v !== null && v !== undefined && v !== '') {
          params.push(`${k}=${encodeURIComponent(v)}`)
        }
      }
      add('paid_at_from', this.formatDateForQuery(this.filters.paid_at_from))
      add('paid_at_to', this.formatDateForQuery(this.filters.paid_at_to))
      const ids = Array.isArray(this.filters.user_ids) ? this.filters.user_ids.filter(Boolean) : []
      ids.forEach(id => params.push(`user_ids[]=${encodeURIComponent(id)}`))
      add('patient_name', this.filters.patient_name)
      add('invoice_no', this.filters.invoice_no)
      add('invoice_type', this.filters.invoice_type)
      add('service_type_id', this.filters.service_type_id)
      return params.length ? `?${params.join('&')}` : ''
    },

    formatDateForQuery(val) {
      if (!val) return ''
      if (typeof val === 'string') return val
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    formatDateTime(val) {
      if (!val) return '—'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return val
      return d.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatIQDCurrency(value) {
      const num = value === null || value === undefined ? 0 : Number(value)
      if (Number.isNaN(num)) return '0 د.ع'
      return `${new Intl.NumberFormat('en-US').format(num)} د.ع`
    },

    /** `total_refund_amount` from API (day row or statistics). */
    refundAmount(row) {
      if (!row || row.total_refund_amount === null || row.total_refund_amount === undefined) return 0
      const n = Number(row.total_refund_amount)
      return Number.isNaN(n) ? 0 : n
    },

    /** Net cash position: total_net_amount minus refunds (aggregates only). */
    netAfterRefund(row) {
      if (!row) return 0
      const net = row.total_net_amount === null || row.total_net_amount === undefined ? 0 : Number(row.total_net_amount)
      const safeNet = Number.isNaN(net) ? 0 : net
      return safeNet - this.refundAmount(row)
    },

    async loadUserOptions() {
      this.loadingUsers = true
      try {
        const response = await this.$http.get('/user?take=500&sort=name')
        const rows = response?.data?.data
        if (Array.isArray(rows)) {
          this.userOptions = rows.map(u => ({ id: u.id, name: u.name }))
        } else {
          this.userOptions = []
        }
      } catch (e) {
        this.userOptions = []
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'خطأ',
            text: e?.response?.data?.message || 'حدث خطأ أثناء تحميل البيانات',
            variant: 'danger',
          },
        })
      } finally {
        this.loadingUsers = false
      }
    },

    async getReportData() {
      this.loading = true
      try {
        const url = `/reports/user-statement${this.buildQueryString()}`
        const response = await this.$http.get(url)
        const payload = response?.data?.data

        if (!response?.data?.status) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'خطأ',
              text: response?.data?.message || response?.data?.error || 'حدث خطأ أثناء تحميل البيانات',
              variant: 'danger',
            },
          })
        }

        this.reportData = payload || null
        this.currentPage = 1
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'خطأ',
            text: error?.response?.data?.message || error?.response?.data?.error || 'حدث خطأ أثناء تحميل البيانات',
            variant: 'danger',
          },
        })
        this.reportData = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

  <style scoped>
  .active-filter-pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
    border-radius: 20px;
    font-size: 0.82rem;
    font-weight: 600;
  }

  .amount-highlight {
    background: rgba(40, 199, 111, 0.08);
    border: 1px solid rgba(40, 199, 111, 0.3);
  }
  </style>
